import type { Brand } from '@/shared/brand/brands';

/** User preference. `system` follows prefers-color-scheme live. */
export type ThemeMode = 'light' | 'dark' | 'system';
/** What is actually painted. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_MODES = ['light', 'dark', 'system'] as const;
export const THEME_STORAGE_KEY = 'numtable.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Browser chrome color per brand × theme. Kept here rather than read from
 * tokens.css because <meta name="theme-color"> needs a literal value.
 */
const THEME_COLOR: Record<Brand, Record<ResolvedTheme, string>> = {
  numtable: { light: '#ffffff', dark: '#0b1120' },
  procalendar: { light: '#ffffff', dark: '#0b1120' },
};

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function prefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return prefersDark() ? 'dark' : 'light';
  return mode;
}

/** Reads the preference the inline script in index.html already looked at. */
export function readStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    // Storage can be unavailable (Safari private mode). Fall through.
  }
  return 'system';
}

export function storeTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Preference simply won't survive a reload. Not worth failing the UI.
  }
}

/**
 * Writes `data-theme` on <html> and updates the theme-color meta tag, which
 * drives the mobile browser chrome and the PWA status bar.
 */
export function applyTheme(mode: ThemeMode, brand: Brand): ResolvedTheme {
  const resolved = resolveTheme(mode);
  document.documentElement.dataset['theme'] = resolved;

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLOR[brand][resolved];

  // Lets the UA style native form controls and scrollbars to match.
  document.documentElement.style.colorScheme = resolved;

  return resolved;
}

/** Subscribes to OS theme changes. Only meaningful while mode is `system`. */
export function watchSystemTheme(onChange: () => void): () => void {
  const mq = window.matchMedia(DARK_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
